import { useEffect, useMemo } from 'react';
import { partition } from 'lodash';
import { ApiTask, TaskStatus, TaskStatusCondition } from '~/api/types';
import { useGetTasks } from '../useGetTasks';
import {
    UseLiveDispatchTasksProps,
    UseLiveDispatchTasksHook,
    FilteredUnassignedTasks
} from './types';
import { useSelector } from 'react-redux';
import { selectTasks } from '~/reducers/tasksSlice';
import { selectSortConfig } from '~/reducers/liveDispatchTableSlice';

export const useLiveDispatchTasks = ({
    selectedTaskIds,
    apiPaginationOptions,
    setTotalLiveDispatchCount
}: UseLiveDispatchTasksProps = {}): UseLiveDispatchTasksHook => {
    const tasks = useSelector(selectTasks);
    const sortConfig = useSelector(selectSortConfig);

    /**
     * unassigned tasks data from TasksApi
     *
     * retrieves any unassigned task, regardless of route date, up to the defined page limit
     */
    const { data: apiTasksData, refetch } = useGetTasks({
        apiOptions: {
            status: [TaskStatus.UNASSIGNED],
            ...apiPaginationOptions,
            sort: sortConfig
        }
    });

    useEffect(() => {
        refetch();
    }, [tasks, refetch]);

    const totalDispatchTasks = apiTasksData?.meta?.total ?? 0;

    /**
     * unassigned tasks data
     */
    const unassignedTasks = useMemo<ApiTask[]>(() => {
        const unassignedTasksData = apiTasksData?.data?.filter((apiTask) => {
            const { status, taskStatus } = apiTask;
            return (
                status === TaskStatus.UNASSIGNED &&
                taskStatus === TaskStatusCondition.UNASSIGNED
            );
        });

        const taskData = unassignedTasksData || [];

        if (setTotalLiveDispatchCount) {
            setTotalLiveDispatchCount(totalDispatchTasks);
        }

        return taskData;
    }, [apiTasksData, setTotalLiveDispatchCount, totalDispatchTasks]);

    /**
     * all unassigned tasks filtered into their respective buckets
     */
    const { isFiltered, selected, excluded } =
        useMemo<FilteredUnassignedTasks>(() => {
            const [selectedTasks, excludedTasks] = partition(
                unassignedTasks,
                ({ id }) => {
                    return selectedTaskIds?.includes(id);
                }
            );

            const filtered = Boolean(selectedTasks.length);

            return {
                isFiltered: filtered,
                selected: selectedTasks,
                excluded: excludedTasks
            };
        }, [unassignedTasks, selectedTaskIds]);

    return {
        isFiltered,
        unassignedTasks: isFiltered ? selected : unassignedTasks,
        excludedUnassignedTasks: isFiltered ? excluded : [],
        totalTasks: totalDispatchTasks
    };
};
